import {
    CENTER_REGION_ID,
    LAYOUT_REGIONS,
    PANEL_KIND_CENTER,
    TAB_PLACEMENT_BOTTOM,
    TAB_PLACEMENT_TOP,
    normalizeDockZone,
} from './constants.js';
import { findNode, findPanelHost } from './layoutTree.js';

export function getCenterSlotChild(root) {
    if (!root) return null;
    if (root.type === 'centerSlot') return root.child;
    const slot = findNode(root, CENTER_REGION_ID)?.node;
    if (slot?.type === 'centerSlot') return slot.child;
    return null;
}

/**
 * Resolve the layout subtree root for a region.
 */
export function getRegionSubtreeRoot(root, regions, region) {
    const r = normalizeDockZone(region);
    const nodeId = regions?.[r];
    if (!nodeId || !root) return null;
    const info = findNode(root, nodeId);
    return info?.node || null;
}

/**
 * Walk tree built by buildLayoutFromPanels and assign region node ids.
 */
export function syncRegionsFromBuild(meta) {
    const regions = {
        left: meta.leftNodeId ?? null,
        right: meta.rightNodeId ?? null,
        top: meta.topNodeId ?? null,
        bottom: meta.bottomNodeId ?? null,
        center: meta.centerNodeId ?? CENTER_REGION_ID,
    };
    return regions;
}

export function subtreeHostsCenter(node) {
    if (!node) return false;
    if (node.type === 'centerSlot') return true;
    if (node.type === 'split') {
        return (node.children || []).some((ch) => subtreeHostsCenter(ch));
    }
    return false;
}

function subtreeIsExclusiveRegionBand(node, region, panelZoneMap) {
    const r = normalizeDockZone(region);
    if (!subtreeHostsZonePanel(node, r, panelZoneMap)) return false;
    for (const other of LAYOUT_REGIONS) {
        if (other === r || other === 'center') continue;
        if (subtreeHostsZonePanel(node, other, panelZoneMap)) return false;
    }
    return true;
}

/**
 * Outermost structural edge band (e.g. panel docked to top root before zone map updates).
 */
export function findStructuralEdgeBand(tree, region) {
    const r = normalizeDockZone(region);
    if (!tree || r === 'center') return null;

    let node = tree;
    while (node?.type === 'split') {
        const children = node.children || [];
        if (children.length < 2) return null;

        let edgeChild = null;
        if (r === 'left' && node.orientation === 'horizontal') {
            edgeChild = children[0];
        } else if (r === 'right' && node.orientation === 'horizontal') {
            edgeChild = children[children.length - 1];
        } else if (r === 'top' && node.orientation === 'vertical') {
            edgeChild = children[0];
        } else if (r === 'bottom' && node.orientation === 'vertical') {
            edgeChild = children[children.length - 1];
        } else {
            break;
        }

        if (!edgeChild) return null;
        if (edgeChild.type === 'panel' || edgeChild.type === 'tabs') {
            return edgeChild;
        }
        if (subtreeHostsCenter(edgeChild)) return null;
        node = edgeChild;
    }

    return null;
}

export function subtreeHostsZonePanel(node, zone, panelZoneMap) {
    if (!node) return false;
    const z = normalizeDockZone(zone);
    if (node.type === 'panel') {
        return panelZoneMap[node.panelId] === z;
    }
    if (node.type === 'tabs') {
        return (node.panelIds || []).some((id) => panelZoneMap[id] === z);
    }
    if (node.type === 'centerSlot') {
        return z === 'center';
    }
    if (node.type === 'split') {
        return (node.children || []).some((ch) =>
            subtreeHostsZonePanel(ch, zone, panelZoneMap),
        );
    }
    return false;
}

/**
 * Outermost node in a layout band (e.g. left column root), not an inner panel node id.
 */
export function findRegionBandRoot(tree, region, panelZoneMap = {}) {
    const r = normalizeDockZone(region);
    if (!tree) return null;

    if (r === 'center') {
        const slot = findNode(tree, CENTER_REGION_ID)?.node;
        if (slot?.type === 'centerSlot') {
            return slot.child || slot;
        }
        if (tree.type === 'centerSlot') {
            return tree.child || tree;
        }
    }

    const walk = (node) => {
        if (!node) return null;

        if (node.type === 'split') {
            const children = node.children || [];
            const hostingChildren = children.filter((ch) =>
                subtreeHostsZonePanel(ch, r, panelZoneMap),
            );

            if (node.orientation === 'horizontal') {
                if (r === 'left' && children[0]) {
                    const ch = children[0];
                    if (subtreeHostsZonePanel(ch, r, panelZoneMap)) return ch;
                }
                if (r === 'right' && children.length > 1) {
                    const ch = children[children.length - 1];
                    if (subtreeHostsZonePanel(ch, r, panelZoneMap)) return ch;
                }
            }
            if (node.orientation === 'vertical') {
                if (r === 'top' && children[0]) {
                    const ch = children[0];
                    if (subtreeHostsZonePanel(ch, r, panelZoneMap)) return ch;
                }
                if (r === 'bottom' && children.length > 1) {
                    const ch = children[children.length - 1];
                    if (subtreeHostsZonePanel(ch, r, panelZoneMap)) return ch;
                }
            }

            if (
                hostingChildren.length > 1 &&
                hostingChildren.every((ch) =>
                    subtreeIsExclusiveRegionBand(ch, r, panelZoneMap),
                )
            ) {
                return node;
            }

            for (const ch of children) {
                const found = walk(ch);
                if (found) return found;
            }
            return null;
        }

        if (node.type === 'panel' && panelZoneMap[node.panelId] === r) {
            return node;
        }
        if (node.type === 'tabs') {
            const has = (node.panelIds || []).some(
                (id) => panelZoneMap[id] === r,
            );
            if (has) return node;
        }
        if (node.type === 'centerSlot' && r === 'center') {
            return node.child || node;
        }

        return null;
    };

    const walked = walk(tree);
    if (
        walked?.type === 'split' &&
        !subtreeHostsCenter(walked) &&
        subtreeIsExclusiveRegionBand(walked, r, panelZoneMap)
    ) {
        return walked;
    }

    const structural = findStructuralEdgeBand(tree, r);
    if (structural) return structural;

    return walked;
}

/**
 * Resolve region anchor: valid regions map entry, else structural band root.
 */
export function resolveRegionRoot(tree, regions, region, panelZoneMap = {}) {
    const r = normalizeDockZone(region);
    const nodeId = regions?.[r];
    if (nodeId && tree) {
        const info = findNode(tree, nodeId);
        if (info?.node) {
            if (info.node.type === 'centerSlot' && info.node.child) {
                return info.node.child;
            }
            return info.node;
        }
    }
    return findRegionBandRoot(tree, r, panelZoneMap);
}

/**
 * Infer region map from tree structure (validated band roots).
 */
export function inferRegionsFromTree(root, panelZoneMap = {}) {
    const regions = {
        left: null,
        right: null,
        top: null,
        bottom: null,
        center: CENTER_REGION_ID,
    };

    if (!root) return regions;

    for (const r of LAYOUT_REGIONS) {
        const band = findRegionBandRoot(root, r, panelZoneMap);
        if (band?.id) regions[r] = band.id;
    }

    const slot = findNode(root, CENTER_REGION_ID)?.node;
    if (slot?.type === 'centerSlot') {
        regions.center = slot.child?.id || slot.id;
    }

    if (!regions.center) {
        regions.center = CENTER_REGION_ID;
    }

    return regions;
}

export function applyPanelKindsToTabs(root, panelsMeta) {
    if (!root) return root;
    if (root.type === 'tabs') {
        const hasCenter = (root.panelIds || []).some(
            (id) => panelsMeta[id]?.panelKind === PANEL_KIND_CENTER,
        );
        return {
            ...root,
            tabPlacement: hasCenter ? TAB_PLACEMENT_TOP : TAB_PLACEMENT_BOTTOM,
        };
    }
    if (root.type === 'split') {
        return {
            ...root,
            children: root.children.map((ch) => applyPanelKindsToTabs(ch, panelsMeta)),
        };
    }
    if (root.type === 'centerSlot') {
        return {
            ...root,
            child: root.child
                ? applyPanelKindsToTabs(root.child, panelsMeta)
                : null,
        };
    }
    return root;
}

export function ensureLayoutStateRegions(state, panelZoneMap = {}) {
    return inferRegionsFromTree(state.root, panelZoneMap);
}

/**
 * Resolve dock target node id from drop overlay hit (handles stale DOM ids).
 */
/**
 * Which layout region (left/right/top/bottom/center) contains a node id.
 */
export function findRegionForNodeId(tree, regions, nodeId) {
    if (!tree || !nodeId) return null;
    for (const r of LAYOUT_REGIONS) {
        const bandId = regions?.[r];
        if (!bandId) continue;
        if (bandId === nodeId) return r;
        const band = findNode(tree, bandId)?.node;
        if (band && nodeContainedInSubtree(band, nodeId)) return r;
        if (band?.type === 'tabs' && nodeId) {
            const targetNode = findNode(tree, nodeId)?.node;
            if (
                targetNode?.type === 'panel' &&
                (band.panelIds || []).includes(targetNode.panelId)
            ) {
                return r;
            }
        }
    }
    if (nodeId === CENTER_REGION_ID) return 'center';
    const slot = findNode(tree, CENTER_REGION_ID)?.node;
    if (slot?.type === 'centerSlot' && nodeContainedInSubtree(slot, nodeId)) {
        return 'center';
    }
    return null;
}

function nodeContainedInSubtree(node, targetId) {
    if (!node) return false;
    if (node.id === targetId) return true;
    if (node.type === 'split') {
        return (node.children || []).some((ch) =>
            nodeContainedInSubtree(ch, targetId),
        );
    }
    if (node.type === 'centerSlot' && node.child) {
        return nodeContainedInSubtree(node.child, targetId);
    }
    return false;
}

/**
 * Layout region (left/right/top/bottom/center) that currently hosts a panel.
 */
export function getLayoutRegionForPanel(tree, regions, panelId) {
    if (!tree || !panelId) return null;
    const info = findPanelHost(tree, panelId);
    if (info?.host?.id) {
        return findRegionForNodeId(tree, regions, info.host.id);
    }
    return null;
}

/**
 * Which layout region contains a dock host hit (panel leaf, tab group, etc.).
 * @param {object} tree
 * @param {Record<string, string|null>} regions
 * @param {{ hostType?: string, nodeId?: string, panelId?: string, targetPanelId?: string }} hit
 */
export function resolveHostLayoutRegion(tree, regions, hit) {
    if (!tree || !hit) return null;
    if (hit.hostType === 'center' || hit.nodeId === CENTER_REGION_ID) {
        return 'center';
    }
    const panelId = hit.targetPanelId || hit.panelId;
    if (panelId) {
        const fromPanel = getLayoutRegionForPanel(tree, regions, panelId);
        if (fromPanel) return fromPanel;
    }
    if (hit.hostType === 'panel' && hit.nodeId) {
        const targetNode = findNode(tree, hit.nodeId)?.node;
        if (targetNode?.type === 'panel' && targetNode.panelId) {
            const fromNodePanel = getLayoutRegionForPanel(
                tree,
                regions,
                targetNode.panelId,
            );
            if (fromNodePanel) return fromNodePanel;
        }
    }
    if (hit.nodeId) {
        const fromNode = findRegionForNodeId(tree, regions, hit.nodeId);
        if (fromNode) return fromNode;
    }
    return null;
}

export function resolveDropTargetNodeId(tree, hit) {
    if (!tree || !hit) return hit?.nodeId || null;

    const { nodeId, hostType, zone, targetPanelId } = hit;

    if (targetPanelId && hostType === 'panel') {
        const host = findPanelHost(tree, targetPanelId);
        if (host?.host?.id) return host.host.id;
    }

    if (hostType === 'tabs' && nodeId && findNode(tree, nodeId)?.node) {
        return nodeId;
    }

    if (
        (hostType === 'center' || nodeId === CENTER_REGION_ID) &&
        normalizeDockZone(zone) === 'center'
    ) {
        return CENTER_REGION_ID;
    }

    if (nodeId && findNode(tree, nodeId)?.node) {
        return nodeId;
    }

    return nodeId || null;
}
